import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { NoClientsState } from "./NoClientsState";
import { ROLE_LABELS, canBill, isReadOnly, isManagerRole } from "../lib/roles";
import { useAuth } from "../context/AuthContext";
import {
  Zap, LayoutDashboard, Users, KanbanSquare, Workflow, MessagesSquare,
  Building2, Settings as SettingsIcon, LogOut, ChevronDown, Menu, X, Check, Search, Bell, Package, Layout, CalendarDays, BarChart3, Megaphone, Star, FileText, Bot, CreditCard, PhoneCall, KeyRound,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/agency", label: "Agency Overview", icon: Building2, agencyOnly: true },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/pipelines", label: "Pipelines", icon: KanbanSquare },
  { to: "/funnels", label: "Funnels", icon: Layout },
  { to: "/forms", label: "Forms", icon: FileText },
  { to: "/calendars", label: "Calendars", icon: CalendarDays },
  { to: "/conversations", label: "Conversations", icon: MessagesSquare },
  { to: "/calls", label: "Calls", icon: PhoneCall },
  { to: "/broadcasts", label: "Broadcasts", icon: Megaphone },
  { to: "/aichat", label: "AI Chat", icon: Bot },
  { to: "/automations", label: "Automations", icon: Workflow },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/reputation", label: "Reputation", icon: Star },
  { to: "/snapshots", label: "Snapshots", icon: Package, agencyOnly: true },
  { to: "/billing", label: "Billing", icon: CreditCard, billingOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const AGENCY_ROLES = ["agency_owner", "agency_admin"];
const ReadOnlyBanner = () => (
  <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300" data-testid="read-only-banner">
    View-only access: you can see everything, but changes are turned off. Ask a manager if you need to edit.
  </div>
);

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppLayout() {
  const { user, tenant, subAccounts, activeSub, switchSubAccount, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const isAgency = AGENCY_ROLES.includes(user?.role);
  const brandColor = activeSub?.branding?.primary_color || tenant?.branding?.primary_color || "#6366F1";

  if (tenant && !["trialing", "active"].includes(tenant.subscription_status)) {
    return <Navigate to="/billing" replace />;
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full" style={{ background: "hsl(var(--sidebar))" }}>
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border/60">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: brandColor }}>
          <Zap className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-display font-bold text-sm truncate">{tenant?.branding?.display_name || tenant?.name || "Momento"}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Agency OS</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="px-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-semibold">Workspace</p>
        {NAV.filter((n) => (!n.agencyOnly || isAgency) && (!n.billingOnly || canBill(user?.role))).map((item) => (
          <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}>
            {({ isActive }) => (
              <>
                <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-primary transition-all duration-200 ${isActive ? "h-6 opacity-100" : "h-0 opacity-0"}`} />
                <item.icon className="w-[18px] h-[18px]" />
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border/60">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">
            {initials(user?.name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-role-label">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
          <button onClick={() => setPwOpen(true)} data-testid="change-password-button" title="Change password"
            className="text-muted-foreground hover:text-foreground transition-colors">
            <KeyRound className="w-4 h-4" />
          </button>
          <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"
            className="text-muted-foreground hover:text-destructive transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background relative z-10">
      <aside className="hidden lg:block w-[260px] shrink-0 border-r border-border/60 h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative w-[260px] h-full"><SidebarContent /></div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-40 glass border-b border-border/60 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-5 h-5" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 h-10 max-w-[240px]" data-testid="sub-account-switcher-button">
                <span className="w-2 h-2 rounded-full pulse-dot shrink-0" style={{ background: brandColor }} />
                <span className="truncate font-medium">{activeSub?.name || "Select client"}</span>
                <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Sub-accounts</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {subAccounts.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => switchSubAccount(s.id)} className="gap-2 cursor-pointer"
                  data-testid={`sub-account-option-${s.id}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.branding?.primary_color || "#6366F1" }} />
                  <span className="flex-1 truncate">{s.name}</span>
                  {s.id === activeSub?.id && <Check className="w-4 h-4 text-primary" />}
                </DropdownMenuItem>
              ))}
              {isAgency && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer text-primary" data-testid="manage-clients-option">
                    + Manage clients
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <div className="hidden md:flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary/50 border border-border/60 text-muted-foreground w-56">
              <Search className="w-4 h-4" />
              <input data-testid="global-search-input" placeholder="Search…" className="bg-transparent outline-none flex-1 text-sm placeholder:text-muted-foreground/60" />
              <kbd className="text-[10px] font-mono bg-background/60 rounded px-1.5 py-0.5 border border-border/60">⌘K</kbd>
            </div>
            <button className="relative w-9 h-9 rounded-lg bg-secondary/50 border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" data-testid="notifications-button">
              <Bell className="w-4 h-4" />
              <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-primary pulse-dot" />
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
          {isReadOnly(user?.role) && <ReadOnlyBanner />}
          {subAccounts.length === 0 && !["/settings", "/billing"].includes(location.pathname)
            ? <NoClientsState isAgency={isManagerRole(user?.role)} />
            : <Outlet />}
        </main>
        <ChangePasswordDialog open={pwOpen || !!user?.must_change_password} onOpenChange={setPwOpen} forced={!!user?.must_change_password} />
      </div>
    </div>
  );
}
